import Select from "../../../shared/_FormInputs/Select";
import {
  FullWHContainer,
  LogbooksInformations,
} from "../../../../pages/Reports/AddReport/addReport.styled";
import { Box, Input, SelectItem, Textarea } from "@mantine/core";
import ReportDatesInputs from "../../../shared/Reports/ReportDatesInputs";
import { Fragment, useMemo, useEffect } from "react";
import useNonMaritime from "../../../../hooks/useNonMaritime";
import { useSelector } from "src/redux/hooks";
import { useGetApi } from "../../../../hooks";
import { getYachtId, getUserId } from "../../../../utils";

interface Props {
  methods: any;
  data: any;
  prefix: string;
  setStartDate: (date: string) => void;
  setEndDate: (date: string) => void;
  resetStamps?: (type: string) => void;
  hideNameAndDesc?: boolean;
  disableL10?: boolean;
}

const ReportInputs = ({
  methods,
  data,
  prefix,
  resetStamps,
  setEndDate,
  setStartDate,
  hideNameAndDesc,
  disableL10,
}: Props) => {
  const { replaceMaritimeSpecialKeys, replaceMaritimeText, nonMaritime } =
    useNonMaritime();
  const uppercase = useSelector(state => state.preferencesSlice.uppercase);
  const { control, register, watch, setValue } = methods;
  const getName = (name: string) => `${prefix}.${name}`;

  const reportStyles = data?.reportStyles as Record<
    string,
    Record<string, string>
  >;

  const type = watch(getName("type"));
  const logbook = watch(getName("logbook"));
  const dates = watch(getName("dates"));

  // Fetch users to build captain/master list
  const { data: usersData } = useGetApi({
    request: "get-users-full",
    body: {
      params: {
        yachtid: getYachtId(),
      },
    },
  });

  const masterRoles = ["Captain/Master"];

  // Filter users to only captains/masters
  const captainList = useMemo(() => {
    const users = (usersData as any)?.list || [];
    return users
      .filter((user: any) => masterRoles.includes(user.rolename))
      .map((user: any) => ({
        value: user.userid,
        label: `${user.ref} - ${user.full_name}`,
      }));
  }, [usersData]);

  // Check if current user is a captain/master
  const currentUserId = getUserId();
  const currentUserIsCaptain = useMemo(() => {
    return captainList.some((captain: any) => captain.value === currentUserId);
  }, [captainList]);

  // Auto-populate captainID field if current user is a captain, or reset it when type changes
  useEffect(() => {
    if (type === "1") {
      if (currentUserIsCaptain) {
        // If current user is a captain, auto-populate their ID
        setValue(getName("captainID"), currentUserId);
      }
    } else {
      // Clear captainID field for non-L10 reports
      setValue(getName("captainID"), "");
    }
  }, [type, currentUserIsCaptain, currentUserId, setValue, prefix]);

  const filteredLbs = useMemo(() => {
    let lbs = data?.lbs ?? [];

    if ((type === "4" || type === "5") && data?.olbs) {
      lbs = data.olbs;
    }
    return lbs;
  }, [data, type]);

  const reportTypes = [
    { value: "0", label: "Full Report" },
    { value: "2", label: "Event Report" },
    { value: "3", label: "Waste Declaration" },
    { value: "4", label: "Crew List Report" },
    { value: "5", label: "Confidential Medical Report" },
    { value: "6", label: "Crew Illness Report" },
    { value: "7", label: "Crew Sign On/Off Report" },
    { value: "8", label: "Hearing Tests Report" },
    { value: "9", label: "Vaccinations Report" },
    { value: "10", label: "Noon Report" },
  ];
  if (!disableL10) {
    reportTypes.push({ value: "1", label: "L10 Report" });
  }
  const availableReportTypes = useMemo(() => {
    return replaceMaritimeSpecialKeys(reportTypes, "value", "reportDropdown");
  }, [replaceMaritimeSpecialKeys]);

  return (
    <Fragment>
      <FullWHContainer>
        <Select
          control={control}
          name={getName("type")}
          maw={"550px"}
          label="Report Type"
          data={availableReportTypes}
          onChange={resetStamps}
        />
        {type !== "4" && type !== "5" && type !== "6" && !nonMaritime && (
          <Select
            control={control}
            name={getName("lb10")}
            maw={"550px"}
            mt={15}
            disabled={type !== "1"}
            label="Ports sourced from"
            data={data?.l10 ?? []}
          />
        )}
        {type === "1" && (
          <Select
            control={control}
            name={getName("maxResults")}
            maw={"550px"}
            label="Max Results"
            data={
              Array(10)
                .fill(0)
                .map((_, i) => ({
                  value: (i + 1) * 10,
                  label: ((i + 1) * 10).toString(),
                })) as unknown as SelectItem[]
            }
          />
        )}
        {type === "1" && !currentUserIsCaptain && (
          <Select
            control={control}
            name={getName("captainID")}
            maw={"550px"}
            mt={15}
            label="Captain/Master"
            placeholder="Select Captain/Master"
            data={captainList}
            searchable
            required
          />
        )}
        {false && reportStyles && type && reportStyles[type] && (
          <Box
            style={{
              display: "flex",
              gap: 12,
              width: "100%",
              marginTop: 15,
              marginBottom: 15,
            }}>
            <Select
              control={control}
              name={getName("displayStyle")}
              style={{ flex: 1 }}
              label="Display Style"
              data={Object.entries(reportStyles[type]).map(([key, value]) => ({
                value: key,
                label: value,
              }))}
            />
            <Select
              control={control}
              name={getName("pdfStyle")}
              style={{ flex: 1 }}
              label="PDF Style"
              data={Object.entries(reportStyles[type]).map(([key, value]) => ({
                value: key,
                label: value,
              }))}
            />
          </Box>
        )}
      </FullWHContainer>

      <LogbooksInformations>
        <Select
          control={control}
          name={getName("logbook")}
          maw={"550px"}
          label="Logbook"
          disabled={
            type === "1" ||
            type === "3" ||
            type === "5" ||
            type === "6" ||
            type === "7" ||
            type === "8" ||
            type === "9"
          }
          data={filteredLbs}
          onChange={resetStamps}
          searchable
        />
        {data?.sections[logbook] &&
          type !== "4" &&
          type !== "5" &&
          type !== "6" &&
          type !== "7" &&
          type !== "8" &&
          type !== "9" && (
            <Box pos="relative">
              <Select
                control={control}
                name={getName("section")}
                styles={{
                  dropdown: {
                    minWidth: "100% !important",
                    width: "max-content !important",
                    maxWidth: "95vw !important",
                  },
                }}
                mt={15}
                label="Section"
                disabled={type === "1" || type === "3"}
                data={data?.sections[logbook]}
              />
            </Box>
          )}
        <Select
          control={control}
          name={getName("status")}
          maw={"550px"}
          mt={15}
          disabled={
            type === "1" ||
            type === "3" ||
            type === "5" ||
            type === "6" ||
            type === "7" ||
            type === "8" ||
            type === "9"
          }
          label="Status"
          data={[
            { value: "0", label: "Open" },
            { value: "1", label: replaceMaritimeText("Yacht Verified") },
            { value: "99", label: "Withdrawn" },
          ]}
        />

        <ReportDatesInputs
          control={control}
          datesSelectorWatchValue={dates}
          startInputName={getName("start")}
          endInputName={getName("end")}
          datesInputName={getName("dates")}
          disabled={
            type === "1" ||
            (data?.olbs && type === "4") ||
            type === "5" ||
            type === "7"
          }
          additionalSetStartDate={setStartDate}
          additionalSetEndDate={setEndDate}
        />
      </LogbooksInformations>
      {!hideNameAndDesc && (
        <FullWHContainer>
          <Input.Wrapper label="Report Name">
            <Input
              disabled={type === "1"}
              maw={"550px"}
              {...register("name", {
                required: type !== "1",
              })}
              styles={{
                input: { textTransform: uppercase ? "uppercase" : "none" },
              }}
            />
          </Input.Wrapper>

          <Textarea
            maw={"550px"}
            disabled={type === "1"}
            mt={15}
            label="Description"
            placeholder="Report description"
            autosize={false}
            minRows={4}
            {...register("desc")}
            styles={{
              input: { textTransform: uppercase ? "uppercase" : "none" },
            }}
          />
        </FullWHContainer>
      )}
    </Fragment>
  );
};
export default ReportInputs;
